'use client';
import { checkSearchLang } from '@/utils/global';
import { createAutocomplete } from '@algolia/autocomplete-core';
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia';
import { useLocale } from 'next-intl';
import { useCallback, useMemo, useRef, useState } from 'react';
import searchClient from '@/utils/algolia/algoliaClient';
import useHandleOutside from '../useHandleOutside';
import { debounced } from '@/utils/algolia/algoliaHelper';

const useAutoComplete = () => {
  const locale = useLocale();
  const popupRef = useRef(null);
  const [autocompleteState, setAutocompleteState] = useState({});

  const searchLang = checkSearchLang(autocompleteState?.query ?? '', locale);

  const productIdx =
    searchLang === 'ar'
      ? process.env.NEXT_PUBLIC_ALGOLIA_IDX_AR
      : process.env.NEXT_PUBLIC_ALGOLIA_IDX_EN;

  const autocomplete = useMemo(
    () =>
      createAutocomplete({
        onStateChange({ state }) {
          setAutocompleteState(state);
        },
        initialState: {
          query: autocompleteState?.query,
        },
        getSources() {
          return debounced([
            {
              sourceId: 'products',
              getItemInputValue({ item }) {
                return item.name;
              },
              getItems({ query }) {
                if (!query) return [];
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: productIdx,
                      params: {
                        query,
                        hitsPerPage: 10,
                      },
                    },
                  ],
                });
              },
            },
          ]);
        },
      }),
    [productIdx]
  );

  const handleQuerySelect = useCallback(
    (query = '') => {
      if (query) {
        autocomplete.setQuery(query);
      }
      autocomplete.setIsOpen(false);
    },
    [autocomplete]
  );

  const closePopup = () => {
    setAutocompleteState((prev) => ({ ...prev, isOpen: false }));
  };

  useHandleOutside(popupRef, closePopup);

  return {
    autocompleteState,
    setAutocompleteState,
    autocomplete,
    handleQuerySelect,
    popupRef,
  };
};

export default useAutoComplete;
